import { Fragment, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import type { ExcelImportCategory, ExcelImportIssue } from '../../services/creditImportService';
import {
  PAGE_SIZE,
  PAGINATE_ABOVE,
  formatHours,
  rowDomId,
  rowStatus,
  sortForReview,
  totalHours,
  type ColumnMapping,
  type ImportRowDraft,
} from '../../utils/creditImportValidation';
import ImportRowEditor from './ImportRowEditor';

interface Props {
  mode: 'self' | 'tenant';
  rows: ImportRowDraft[];
  issuesByRow: Record<number, ExcelImportIssue[]>;
  categories: ExcelImportCategory[];
  deliveryMethods: string[];
  columnMapping: ColumnMapping;
  /** Firm mode: rows whose edited email the server is still re-matching. */
  pendingEmailRows?: Set<number>;
  onRowChange: (row: ImportRowDraft) => void;
  onToggleInclude: (rowNumber: number, include: boolean) => void;
  onRemoveRow: (rowNumber: number) => void;
  onRestoreRow: (rowNumber: number) => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The review list in the CPE Reporting ledger vocabulary: one row per activity, hours as chips per field of
 * study, no horizontal scroll. Exceptions sort first and start expanded with their message and the fields to
 * fix; duplicates follow, unchecked; ready rows carry no mark (the only marks on the page are decisions).
 */
export default function ImportRowList({
  mode,
  rows,
  issuesByRow,
  categories,
  deliveryMethods,
  columnMapping,
  pendingEmailRows,
  onRowChange,
  onToggleInclude,
  onRemoveRow,
  onRestoreRow,
}: Props) {
  const sorted = useMemo(() => sortForReview(rows, issuesByRow), [rows, issuesByRow]);
  const paginate = sorted.length > PAGINATE_ABOVE;
  const [page, setPage] = useState(0);
  // Clamp rather than reset in an effect: a removed page simply shows the first one.
  const safePage = page * PAGE_SIZE >= sorted.length ? 0 : page;
  const visible = paginate ? sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE) : sorted;

  // Rows that need attention are open unless the user collapsed them (so a row that becomes an exception
  // later, e.g. after a column is ignored or the server refuses a commit, opens by itself). Ready rows are
  // closed unless the user opened them. Both sets are keyed by sheet row number.
  const [userOpened, setUserOpened] = useState<Set<number>>(() => new Set());
  const [userCollapsed, setUserCollapsed] = useState<Set<number>>(() => new Set());
  const isRowOpen = (row: ImportRowDraft, hasIssues: boolean) =>
    !row.removed && (userOpened.has(row.rowNumber) || (hasIssues && !userCollapsed.has(row.rowNumber)));
  const toggle = (row: ImportRowDraft, hasIssues: boolean) => {
    const open = isRowOpen(row, hasIssues);
    setUserOpened((prev) => {
      const next = new Set(prev);
      if (open) next.delete(row.rowNumber);
      else next.add(row.rowNumber);
      return next;
    });
    setUserCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.add(row.rowNumber);
      else next.delete(row.rowNumber);
      return next;
    });
  };

  // An edit pins the row open: fixing the last issue must not collapse the editor under the user's hands.
  const handleEditorChange = (draft: ImportRowDraft) => {
    setUserOpened((prev) => (prev.has(draft.rowNumber) ? prev : new Set(prev).add(draft.rowNumber)));
    onRowChange(draft);
  };

  const categoryName = (id: string) => categories.find((c) => String(c.id) === id)?.name ?? `Category ${id}`;
  const columnCount = mode === 'tenant' ? 8 : 7;

  return (
    <Box>
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'surface.raised' }}>
        <Table size="small" aria-label="Rows to import">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} padding="checkbox" />
              <TableCell>Activity</TableCell>
              {mode === 'tenant' && <TableCell>Email</TableCell>}
              <TableCell sx={{ width: 120 }}>Date</TableCell>
              <TableCell>Hours by field of study</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>Total</TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Include</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const issues = issuesByRow[row.rowNumber] ?? [];
              const status = rowStatus(issues, row.include, row.removed);
              const isOpen = isRowOpen(row, issues.length > 0);
              const total = totalHours(row, columnMapping);
              const muted = status === 'skipped' || status === 'removed';
              const titleId = `${rowDomId(row.rowNumber)}-title`;
              return (
                <Fragment key={row.rowNumber}>
                  <TableRow
                    id={rowDomId(row.rowNumber)}
                    hover={!row.removed}
                    data-status={status}
                    onClick={() => { if (!row.removed) toggle(row, issues.length > 0); }}
                    sx={{
                      cursor: row.removed ? 'default' : 'pointer',
                      '& td': { borderBottom: isOpen ? 0 : undefined },
                      ...(row.removed ? { '& td': { textDecoration: 'line-through', color: 'text.disabled' } } : {}),
                      ...(status === 'skipped' ? { '& td': { color: 'text.secondary' } } : {}),
                    }}
                  >
                    {/* Mark: only decisions are marked. Exceptions red, duplicates neutral, ready nothing. */}
                    <TableCell padding="checkbox">
                      {status === 'error' ? (
                        <Tooltip title={issues.map((i) => i.message).join(' ')}>
                          <ErrorOutlineIcon fontSize="small" sx={{ color: 'status.attention.fg', display: 'block' }} aria-label="Needs attention" role="img" />
                        </Tooltip>
                      ) : !row.removed ? (
                        <IconButton
                          size="small"
                          aria-label={isOpen ? `Collapse row ${row.rowNumber}` : `Edit row ${row.rowNumber}`}
                          aria-expanded={isOpen}
                          aria-controls={`${rowDomId(row.rowNumber)}-editor`}
                          onClick={(e) => { e.stopPropagation(); toggle(row, issues.length > 0); }}
                        >
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" id={titleId} sx={{ fontWeight: 500, color: muted ? 'inherit' : 'text.primary', overflowWrap: 'anywhere' }}>
                        {row.activityTitle?.trim() || <em>Untitled</em>}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                        {[row.providerName, `Row ${row.rowNumber}`].filter(Boolean).join(' · ')}
                        {row.isDuplicate && (
                          <Chip
                            size="small"
                            label={mode === 'tenant' ? 'Already in their history' : 'Already in your history'}
                            sx={{ ml: 1, height: 20, fontSize: '0.6875rem', bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg' }}
                          />
                        )}
                      </Typography>
                    </TableCell>

                    {mode === 'tenant' && (
                      <TableCell>
                        {pendingEmailRows?.has(row.rowNumber) ? (
                          <>
                            <Typography variant="body2" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>{row.email}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Checking…</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: row.userId || muted ? 'inherit' : 'status.attention.fg', overflowWrap: 'anywhere' }}>
                            {row.email || <em>Missing</em>}
                          </Typography>
                        )}
                      </TableCell>
                    )}

                    <TableCell>
                      {row.dateEarned ? (
                        <Typography variant="body2">{formatDate(row.dateEarned)}</Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'status.attention.fg', fontStyle: row.dateEarnedRaw ? 'normal' : 'italic' }}>
                          {row.dateEarnedRaw || 'Missing'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(row.hours).map(([id, h]) => (
                          <Chip
                            key={id}
                            size="small"
                            label={`${categoryName(id)} ${formatHours(h)}`}
                            sx={{
                              bgcolor: issues.some((i) => i.field === `hours:${id}`) ? 'status.attention.bg' : 'tint.primarySoft',
                              color: issues.some((i) => i.field === `hours:${id}`) ? 'status.attention.fg' : 'primary.dark',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                        {Object.entries(row.unmappedHours).map(([header, h]) => {
                          const mapped = columnMapping[header];
                          if (mapped === 'ignore') return null;
                          const label = typeof mapped === 'number' ? `${categoryName(String(mapped))} ${formatHours(h)}` : `${header} ${formatHours(h)}`;
                          return (
                            <Chip
                              key={`u-${header}`}
                              size="small"
                              variant={mapped === undefined ? 'outlined' : 'filled'}
                              label={label}
                              sx={mapped === undefined
                                ? { color: 'status.attention.fg', borderColor: 'status.attention.fg', fontWeight: 600 }
                                : { bgcolor: 'tint.primarySoft', color: 'primary.dark', fontWeight: 600 }}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: muted ? 'inherit' : 'primary.dark' }}>
                        {formatHours(total)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={row.include && !row.removed}
                        disabled={row.removed}
                        inputProps={{ 'aria-label': `Include row ${row.rowNumber}` }}
                        onChange={(_, checked) => onToggleInclude(row.rowNumber, checked)}
                      />
                    </TableCell>

                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      {row.removed ? (
                        <Tooltip title="Restore row">
                          <IconButton size="small" aria-label={`Restore row ${row.rowNumber}`} onClick={() => onRestoreRow(row.rowNumber)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Remove row">
                          <IconButton size="small" aria-label={`Remove row ${row.rowNumber}`} onClick={() => onRemoveRow(row.rowNumber)} sx={{ color: 'text.secondary' }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {isOpen && (
                    <TableRow id={`${rowDomId(row.rowNumber)}-editor`}>
                      <TableCell />
                      <TableCell colSpan={columnCount - 1} sx={{ pt: 0 }}>
                        <ImportRowEditor
                          mode={mode}
                          row={row}
                          issues={issues}
                          categories={categories}
                          deliveryMethods={deliveryMethods}
                          columnMapping={columnMapping}
                          emailPending={pendingEmailRows?.has(row.rowNumber)}
                          onChange={handleEditorChange}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {paginate && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) => `Rows ${from}–${to} of ${count}`}
        />
      )}
    </Box>
  );
}
